import React, { useCallback, useState } from "react";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { Avatar, ButtonIcon, ButtonStandard, Chip, ProgressBar, Text } from "@performyard/py-components";
import { ApproveBudgetData } from "@performyard/service-contracts";

import { BudgetReviewExpandedDecisionTable } from "./BudgetReviewExpandedDecisionTable";

import {
	useApproveBudget,
	useCompanySalaryFieldId,
	useCurrentUser,
	useRbacPrimaryRolePermissions,
} from "../../../../../api/hooks";

import { formatCurrencyFull } from "../../../utils";
import {
	ManagerBudgetGroupWithCycleAllotment,
	computeAdjustedBudgetFigures,
	mapBudgetGroupResultsToDecisionRows,
	pluralize,
} from "../../SharedComponents/utils";

import { resolveGroupBudget, sumAmounts, zeroWireMoney } from "../../SharedComponents/money-aggregation";

type ApprovalAccordionProps = {
	budget: ManagerBudgetGroupWithCycleAllotment;
	compensationCycleId: string;
	cycleStatus: string;
	defaultExpanded?: boolean;
};

type BudgetGroupStatus = ManagerBudgetGroupWithCycleAllotment["status"];

type BudgetStatusChip = {
	label: string;
	color: "primary" | "green" | "red" | "yellow" | "neutral";
};

const budgetGroupStatusChip: Record<BudgetGroupStatus, BudgetStatusChip> = {
	pending: { label: "Not Submitted", color: "neutral" },
	in_review: { label: "Submitted", color: "yellow" },
	approved: { label: "Approved", color: "green" },
	blocked: { label: "Blocked", color: "red" },
	submitted: { label: "Submitted", color: "primary" },
};

type ManagerDisplayProps = {
	managerId: string;
	managerName: string | null;
	managerSubtitle: string;
};

const ManagerDisplay: React.FC<ManagerDisplayProps> = ({ managerId, managerName, managerSubtitle }) => {
	return (
		<div className="flex min-w-0 flex-row items-center gap-2">
			<Avatar size={32} id={managerId || ""} name={managerName ?? ""} />
			<div className="flex min-w-0 flex-col">
				<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
					{managerName}
				</Text>
				<Text size="paragraph-3" weight="regular" color="neutral-6">
					{managerSubtitle}
				</Text>
			</div>
		</div>
	);
};

export const ApprovalAccordion: React.FC<ApprovalAccordionProps> = ({
	budget,
	compensationCycleId,
	cycleStatus,
	defaultExpanded = false,
}) => {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const toggleExpanded = useCallback(() => {
		setIsExpanded((open) => !open);
	}, []);
	const { data: currentUser } = useCurrentUser();
	const { data: salaryFieldData } = useCompanySalaryFieldId();
	const salaryFieldId = salaryFieldData?.salaryCustomFieldId;
	const { data: permissions } = useRbacPrimaryRolePermissions("compensation");
	const budgetReviewInfoLinksReadOnly = !permissions?.includes("compensation:useInfoLinks");
	const approverId = currentUser?.me?.authorization?.payload?.user_id ?? "";
	// Map to the request's row shape rather than forwarding the manager group as-is.
	// The group carries manager metadata and response-shaped rows; only the per-employee
	// amounts belong in the request, and they go back as the same exact wire money.
	const approveBudgetRows: ApproveBudgetData = budget.results.map((row) => ({
		employeeId: row.employeeId.toString(),
		proposedIncreaseAmount: row.proposedIncreaseAmount ?? zeroWireMoney,
		proposedIncreasePercent: row.proposedIncreasePercent ?? 0,
		proposedSalary: row.proposedSalary ?? zeroWireMoney,
	}));
	const { mutate: approveBudgetMutate, isPending: isApproveBudgetPending } = useApproveBudget(
		compensationCycleId,
		approverId,
		approveBudgetRows
	);
	const canAlwaysViewSalary = Boolean(permissions?.includes("compensation:canAlwaysViewSalary"));
	const managerId = budget.managerId;
	const managerName = budget.managerName;
	const actingManagerId = budget.overrideManagerId;
	const actingManagerName = budget.overrideManagerName;
	const budgetManagerCount = budget.managerDirectReportCount;
	const budgetManagerTitle = budget.managerJobTitle.trim();
	const cycleIdForChildTables = compensationCycleId || String(budget.compensationCycle ?? "");
	const managerSubtitle =
		budgetManagerTitle.length > 0
			? `${budgetManagerTitle} • ${budgetManagerCount} ${pluralize(budgetManagerCount, "Direct Report", "Direct Reports")}`
			: `${budgetManagerCount} ${pluralize(budgetManagerCount, "Direct Report", "Direct Reports")}`;

	// The budget allocated and snapshotted at creation; older cycles without one
	// fall back to the cycle percent of the group's salary total.
	const groupIncreaseBudget = resolveGroupBudget(
		budget.groupIncreaseBudget,
		sumAmounts(budget.results.map((row) => row.currentSalary)),
		budget.allotmentIncreasePercent
	);

	const baseRows = mapBudgetGroupResultsToDecisionRows(budget.results);
	const figures = computeAdjustedBudgetFigures(baseRows, {}, groupIncreaseBudget);

	const { groupAllocated, groupPercentAllocated, isGroupOverBudget } = figures;

	const { label: budgetStatusLabel, color: budgetStatusColor } = budgetGroupStatusChip[budget.status];
	const showApproveButton = budget.status === "in_review" && cycleStatus === "active";
	const hasBudgetLines = budget.results.length > 0;

	const handleApproveBudget = () => {
		approveBudgetMutate();
	};

	return (
		<div
			className="w-full max-w-full min-w-0 shrink-0 overflow-hidden rounded-lg border border-solid border-neutral-2 bg-neutral-0"
			data-manager-id={managerId}
		>
			<div className="box-border flex min-h-[56px] flex-row items-center self-stretch px-6 py-2">
				<div
					className={`inline-flex shrink-0 origin-center transition-transform duration-200 ease-out ${
						isExpanded ? "rotate-180" : "rotate-0"
					}`}
				>
					<ButtonIcon
						icon={faChevronDown}
						size={24}
						color="neutral-7"
						onClick={toggleExpanded}
						disabled={!hasBudgetLines}
						aria-expanded={isExpanded}
						aria-label={isExpanded ? "Collapse budget details" : "Expand budget details"}
					/>
				</div>
				<div className="flex min-w-0 flex-1 flex-row items-start gap-2 pl-4">
					<ManagerDisplay managerId={managerId} managerName={managerName} managerSubtitle={managerSubtitle} />
					{actingManagerId ? (
						<div className="shrink-0 self-center pl-4">
							<Chip
								label="Acting Manager"
								employeeId={actingManagerId}
								employeeName={actingManagerName ?? ""}
								color="neutral"
								size={24}
								employeeTextSize={"caption"}
							/>
						</div>
					) : null}
				</div>
				<div className="flex shrink-0 flex-row items-center gap-4 self-center pl-6">
					<div className="flex w-52 shrink-0 flex-col gap-1">
						<div className="flex w-full flex-row items-center justify-between gap-2">
							<div className="min-w-0 flex-1 overflow-hidden text-left">
								<Text size="caption" weight="regular" color="neutral-9">
									{`${formatCurrencyFull(groupAllocated)}/${formatCurrencyFull(groupIncreaseBudget)}`}
								</Text>
							</div>
							<div className="shrink-0 text-right tabular-nums">
								<Text size="caption" weight="regular" color="neutral-9">
									{`${groupPercentAllocated.toFixed(0)}%`}
								</Text>
							</div>
						</div>
						<ProgressBar
							value={Math.min(100, groupPercentAllocated)}
							color={isGroupOverBudget ? "red" : "neutral"}
						/>
					</div>
					<div className="flex shrink-0 flex-row items-center gap-[16px]">
						<Chip label={budgetStatusLabel} color={budgetStatusColor} size={24} />
						{showApproveButton && (
							<ButtonStandard
								variant="outline"
								color="neutral"
								size="small"
								onClick={handleApproveBudget}
								disabled={isApproveBudgetPending || !approverId}
							>
								Approve
							</ButtonStandard>
						)}
					</div>
				</div>
			</div>
			{isExpanded && hasBudgetLines ? (
				<div className="max-w-full min-w-0 overflow-x-hidden px-4">
					<BudgetReviewExpandedDecisionTable
						budget={budget}
						figures={figures}
						compensationCycleId={cycleIdForChildTables}
						cycleStatus={cycleStatus}
						salaryFieldId={salaryFieldId}
						budgetReviewInfoLinksReadOnly={budgetReviewInfoLinksReadOnly}
						canAlwaysViewSalary={canAlwaysViewSalary}
					/>
				</div>
			) : null}
		</div>
	);
};
